import { supabase } from "../lib/supabase";

// supabase-js's functions.invoke() error.message is always the generic
// "Edge Function returned a non-2xx status code" for any non-2xx response —
// the real JSON error body written by stripe-cards/index.ts (e.g. "Card not
// found", "Payment method does not belong to this advertiser") is on
// error.context and has to be unwrapped explicitly.
async function extractFunctionErrorMessage(error) {
  try {
    if (error?.context?.json) {
      const body = await error.context.clone().json();
      if (body?.error) return body.error;
    }
  } catch {
    // fall through to the generic message below
  }
  return error?.message ?? "stripe-cards function error";
}

/**
 * All stripe-cards actions are routed through the single Edge Function.
 * The caller must supply the advertiser_id and an auth token from the session.
 */
async function invoke(action, advertiserId, authToken, extra = {}) {
  if (!supabase) {
    throw new Error("Supabase client is not initialized");
  }
  const { data, error } = await supabase.functions.invoke("stripe-cards", {
    body: { action, advertiser_id: advertiserId, ...extra },
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (error) {
    throw new Error(await extractFunctionErrorMessage(error));
  }
  return data;
}

/**
 * Re-lists PaymentMethods for the advertiser's already-linked Stripe
 * Customer and upserts them. No discovery — if the advertiser has no
 * stripe_customer_id yet, this is a no-op that returns matched: false.
 * Shape: { matched: boolean, reason?, cards: [] }
 */
export async function syncCards(advertiserId, authToken) {
  return invoke("sync", advertiserId, authToken);
}

/**
 * Creates (or reuses) a Stripe Customer for this advertiser and returns a
 * SetupIntent client secret for the frontend to confirm with
 * stripe.confirmCardSetup. Shape: { client_secret: string }
 */
export async function createSetupIntent(advertiserId, authToken) {
  return invoke("create-setup-intent", advertiserId, authToken);
}

/**
 * Persists a card after client-side SetupIntent confirmation.
 * Shape: { card: {...} }
 */
export async function attachCard(advertiserId, authToken, paymentMethodId) {
  return invoke("attach-card", advertiserId, authToken, {
    payment_method_id: paymentMethodId,
  });
}

/**
 * Marks the given card as primary, unsetting any previous primary.
 * Shape: { success: boolean }
 */
export async function setPrimaryCard(advertiserId, authToken, cardId) {
  return invoke("set-primary", advertiserId, authToken, { card_id: cardId });
}

/**
 * Detaches the card from Stripe and deletes it; auto-promotes the oldest
 * remaining card to primary if the deleted card was primary.
 * Shape: { success: boolean }
 */
export async function deleteCard(advertiserId, authToken, cardId) {
  return invoke("delete-card", advertiserId, authToken, { card_id: cardId });
}

/**
 * A plain dollar-amount balance — the advertiser's most recent automated
 * charge attempt (if still failing) minus any succeeded manual payments made
 * since. No conversion-level tracking. Shape: { amount_owed: number }
 */
export async function getAmountOwed(advertiserId, authToken) {
  return invoke("amount-owed", advertiserId, authToken);
}

/**
 * The picker's count + total for the "pay oldest N" flow (DEV-2116 WU-3). No
 * p_limit -> the full current-month unpaid picture; with p_limit -> the
 * running total for the oldest N conversions a given count would select.
 * Shape: { unpaid_count, unpaid_total, oldest_created_at }
 */
export async function getChargeableConversionsSummary(advertiserId, authToken, limit) {
  return invoke("chargeable-conversions", advertiserId, authToken, limit != null ? { p_limit: limit } : {});
}

/**
 * Charges the given card for the oldest `conversionCount` unpaid, chargeable,
 * current-month conversions (DEV-2116 WU-3 — count-based, not a dollar
 * amount). The server resolves the exact conversion set and amount; a count
 * larger than what's available is clamped to pay everything that exists. On
 * the common path (no interactive authentication required) this is already
 * terminal — check `success`. If the card's issuer requires 3D Secure,
 * returns `{ requires_action: true, client_secret, payment_intent_id }`
 * instead; the caller must complete the challenge
 * (stripe.confirmCardPayment) and then call payNowFinalize with the same
 * payment_intent_id.
 * Shape: { success, amount_owed_remaining, failure_message? } |
 *        { requires_action: true, client_secret, payment_intent_id }
 */
export async function payNow(advertiserId, authToken, cardId, conversionCount) {
  return invoke("pay-now", advertiserId, authToken, { card_id: cardId, conversion_count: conversionCount });
}

/**
 * Records the terminal outcome of a payment_intent_id from payNow after a
 * 3D Secure challenge (or any) has resolved client-side — the server always
 * re-verifies the real status with Stripe rather than trusting the client.
 * Safe to call more than once for the same payment_intent_id.
 * Shape: { success: boolean, amount_owed_remaining: number, failure_message? }
 */
export async function payNowFinalize(advertiserId, authToken, paymentIntentId) {
  return invoke("pay-now-finalize", advertiserId, authToken, { payment_intent_id: paymentIntentId });
}

/**
 * Support-only (DEV-2116 WU-5). Clears advertiser_auto_charge_settings'
 * paused_reason / paused_at / failure_count. Idempotent. Does NOT resume
 * campaigns and does NOT change advertisers.status — those stay with the
 * Advertiser Settings modal's own Save.
 * Shape: { resolved, enabled, paused_reason, paused_at, failure_count }
 */
export async function resolveBillingPause(advertiserId, authToken) {
  return invoke("resolve-billing-pause", advertiserId, authToken);
}

/**
 * Support-only (DEV-2116 WU-5). Records a payment finance collected out of
 * band as a source='external' advertiser_payments row and links the still-
 * unpaid conversions of the selected failed attempt's window to it. No
 * Stripe call. The recorded amount and the linked conversion total are
 * independent and may differ by design.
 * Shape: { payment_id, amount_recorded, linked_count, linked_spend_total, window }
 */
export async function recordOfflinePayment(advertiserId, authToken, { failedPaymentId, amount, note }) {
  return invoke("record-offline-payment", advertiserId, authToken, {
    failed_payment_id: failedPaymentId, amount, note,
  });
}
